import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MOBILE_ANALYSIS_SHEET_EXPANDED_MAX_FRACTION } from './analysisMobileSheetConstants';

export function useAnalysisMobileBottomSheet(isSmUp: boolean) {
    const [sheetOpen, setSheetOpen] = useState(true);
    const [measureEl, setMeasureEl] = useState<HTMLDivElement | null>(null);
    
    // スナップポイントのピクセル値（数値で管理して最後に px 文字列にする）
    const minHeight = 32; // ハンドル部分の高さ
    const [peekHeight, setPeekHeight] = useState(280); // ツールバーまでの高さ
    const [expandedHeight, setExpandedHeight] = useState(480); // 70%の高さ

    const [snapPoint, setSnapPoint] = useState<number | string | null>('280px');

    const peekContentRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const updateHeights = () => {
            // コンテナ（measureEl）の高さ、または画面高さ
            const containerH = measureEl ? measureEl.getBoundingClientRect().height : window.innerHeight;
            
            // コンテナ高さの70%を最大展開高さとする
            const maxH = Math.floor(containerH * MOBILE_ANALYSIS_SHEET_EXPANDED_MAX_FRACTION);
            setExpandedHeight(maxH);

            if (peekContentRef.current) {
                const ph = peekContentRef.current.getBoundingClientRect().height;
                // ピーク高さ = ハンドル(32px) + ツールバー等の高さ
                const calculatedPeek = Math.min(ph + 32, maxH * 0.9);
                setPeekHeight(calculatedPeek);
            }
        };

        updateHeights();
        const ro = new ResizeObserver(updateHeights);
        if (peekContentRef.current) ro.observe(peekContentRef.current);
        if (measureEl) ro.observe(measureEl);
        window.addEventListener('resize', updateHeights);
        
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateHeights);
        };
    }, [measureEl]);

    const mobileMinHeightPx = `${minHeight}px`;
    const mobilePeekHeightPx = `${peekHeight}px`;
    const mobileExpandedHeightPx = `${expandedHeight}px`;

    // PC表示時のリセット
    useEffect(() => {
        if (isSmUp) {
            setSnapPoint(mobilePeekHeightPx);
            setSheetOpen(true);
        }
    }, [isSmUp, mobilePeekHeightPx]);

    // 不正なスナップポイントの補正（リサイズ等で高さが変わった時に追従させる）
    useEffect(() => {
        if (snapPoint === null) return;
        const valid = [mobileMinHeightPx, mobilePeekHeightPx, mobileExpandedHeightPx];
        setSnapPoint((cur) => {
            if (cur == null) return mobilePeekHeightPx;
            const k = typeof cur === 'number' ? `${cur}px` : String(cur).trim();
            if (valid.includes(k)) return cur;
            return mobilePeekHeightPx;
        });
    }, [mobileMinHeightPx, mobilePeekHeightPx, mobileExpandedHeightPx, snapPoint]);

    const isExpanded = useMemo(() => {
        if (snapPoint === null) return false;
        // 数値または '123px' 形式から数値を取り出して比較（1px程度の誤差を許容）
        const s = typeof snapPoint === 'number' ? snapPoint : parseInt(String(snapPoint), 10);
        const target = parseInt(mobileExpandedHeightPx, 10);
        return !isNaN(s) && !isNaN(target) && s >= target - 1;
    }, [snapPoint, mobileExpandedHeightPx]);

    return {
        sheetOpen,
        setSheetOpen,
        measureEl,
        setMeasureEl,
        mobileMinHeightPx,
        mobilePeekHeightPx,
        mobileExpandedHeightPx,
        snapPoint,
        setSnapPoint,
        peekContentRef,
        isExpanded,
    };
}
